import { Component, EventEmitter, OnDestroy, OnInit, Output, ViewChild, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgbTypeahead, NgbTypeaheadSelectItemEvent } from '@ng-bootstrap/ng-bootstrap';
import { filter, map, merge, Observable, Subscription, tap } from 'rxjs';
import { EverythingAutocompleteSearchService } from '../../everything-autocomplete-search.service';
import { EverythingSearchResult, isEverythingSearchResult } from '../../model/everything-search-result';
import { SourcePipe } from '../../pipe/source.pipe';
import { SearchParamsContainerComponent } from '../search-params-container/search-params-container.component';
import { SearchParamsMapperService } from '../search-params-mapper.service';

@Component({
  selector: 'app-search-filter-query',
  templateUrl: './search-filter-query.component.html',
  styleUrls: [ './search-filter-query.component.scss' ],
  imports: [ FormsModule, NgbTypeahead, SourcePipe ]
})
export class SearchFilterQueryComponent implements OnInit, OnDestroy {
  container = inject(SearchParamsContainerComponent);
  private searchService = inject(EverythingAutocompleteSearchService);
  private mapper = inject(SearchParamsMapperService);

  @ViewChild(NgbTypeahead) typeahead: NgbTypeahead;
  @Output() instantSearchResultClicked = new EventEmitter<EverythingSearchResult>();
  focus = new EventEmitter<string>();
  input: string;
  value: string;
  autocomplete = true;
  private subscription: Subscription;

  ngOnInit() {
    this.subscription = this.container.paramsChange.subscribe({
      next: ({ query }) => this.input = this.value = query
    });
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  setParams(query: string): void {
    this.autocomplete = false;
    this.input = query;
    setTimeout(() => this.typeahead.dismissPopup());
    if (this.input !== this.value) {
      this.container.setParams({ query });
    }
  }

  /**
   * Hides autocomplete results after the form has been submitted
   * Autocomplete is re-enabled when there is new input
   */
  autocompleteSearch = (input: Observable<string>): Observable<(SuggestionResult)[]> => {
    input = merge(this.focus, input).pipe(
      filter(value => value?.length > 0)
    );
    return merge(
      // show loading state after typing
      input.pipe(
        map(() => new Array(5))
      ),
      // run search
      this.searchService.search(
        input.pipe(
          tap(() => this.searchService
            .withParams(this.mapper.toEverythingSearchParams(this.container.params))
            .withCount(5)
          )
        )
      )
    ).pipe(
      map(results => this.input ? [ { query: this.input }, ...results ] : []),
      // hide typeahead on form submit
      filter(() => {
        const autocomplete = this.autocomplete;
        this.autocomplete = true;
        return autocomplete;
      })
    );
  };

  handleSelectItem(event: NgbTypeaheadSelectItemEvent) {
    const result = event.item as SuggestionResult;
    event.preventDefault();

    if (isEverythingSearchResult(result)) {
      this.instantSearchResultClicked.emit(result);
    } else if (result?.query) {
      this.setParams(result.query);
    }
  }

}

type SuggestionResult = EverythingSearchResult | { query: string } | undefined;
